import { createAPIHeaders, fetchUtility } from '.';
import { PipelineLevels, UploadReason } from '../../types/generic';
import { ForceDetails, UploadHeader } from '../../types/upload';
import { UserDetails } from '../../types/user';
import {
	anprTopics,
	axiomTopics,
	cellebriteSheetTopics,
	cellebriteXmlTopics,
	stellarCellebriteXMLTopics,
	SubscriptionOptions,
} from '../processorTopics';

/**
 * Massively over-engineered way to get the force a user belongs to
 *
 * Check every organisation provided against forceDetails and if any key exists in the org aliases list then add the corresponding value to a tracker
 *
 * Return mapping that occurred the most in the user's list
 * @param orgs List of user's organisations
 * @param forceDetails Record of the Force Details provided by the API
 */
export const getForceName = (orgs: string[], forceDetails: ForceDetails): string => {
	const orgGuesses: Record<string, number> = {};

	if (orgs.length > 0) {
		orgs.forEach((org) => {
			Object.entries(forceDetails).forEach(([forceName, { forceAliases }]) => {
				if (forceAliases.some((alias) => org.toLocaleLowerCase() === alias.toLocaleLowerCase())) {
					if (forceName in orgGuesses) {
						orgGuesses[forceName] += 1;
					} else {
						orgGuesses[forceName] = 1;
					}
				}
			});
		});

		if (Object.keys(orgGuesses).length > 0) {
			return Object.keys(orgGuesses).reduce((a, b) => (orgGuesses[a] > orgGuesses[b] ? a : b));
		} else {
			return 'Default';
		}
	} else {
		return 'Default';
	}
};

export const createUploadHeader = (
	deviceOwner: string,
	fileLocation: string,
	userDetails: UserDetails,
	operationId: string,
	uploadReason: UploadReason,
	forceDetails: ForceDetails,
	useCase: string,
	deviceOwnerType: string
): UploadHeader => {
	const orgNames = userDetails.orgs.map((org) => org.name);
	// do NOT get/use rrd days from forceDetails here, as this can be manipulated by the user to bypass rrd policy
	return {
		deviceOwner,
		deviceOwnerType,
		fileLocation,
		uploaderName: userDetails.name,
		uploaderEmail: userDetails.email,
		uploaderOrgs: orgNames,
		uploadTime: Math.floor(new Date().getTime() / 1000),
		operationId,
		forceName: getForceName(orgNames, forceDetails),
		uploadReason,
		useCase,
	};
};

export const getPipelineDate = (curTime?: number): string => {
	if (curTime) {
		return new Date(curTime).toISOString().replace('T', ' ').replaceAll('.', ',');
	} else {
		return new Date(Date.now()).toISOString().replace('T', ' ').replaceAll('.', ',');
	}
};

/**
 * Formats a string in the same style as the messages that the Pipeline creates
 * @param err The error message to be included
 * @returns
 */
export const buildPipelineMessage = (err: string, status: PipelineLevels): string => {
	return `${getPipelineDate()} | ${status} | ${err}`;
};

export const publishMessage = <T extends UploadHeader>(
	message: T,
	topicName: string,
	fileId: string,
	apiKey: string,
	duplicatePreference: string
): Promise<Response> => {
	return fetchUtility(`${import.meta.env.VITE_API_URL}/kafka/publish`, {
		headers: createAPIHeaders(apiKey, 'POST'),
		method: 'POST',
		body: JSON.stringify({
			topicName,
			messageKey: fileId,
			message,
			duplicatePreference,
		}),
	});
};

export const sendSubscribeMessage = (subscriptionTarget: SubscriptionOptions, apiKey: string): Promise<Response> => {
	let topics = {};
	switch (subscriptionTarget) {
		case SubscriptionOptions.CELLEBRITE_SHEET:
			topics = cellebriteSheetTopics;
			break;
		case SubscriptionOptions.CELLEBRITE_XML:
			topics = cellebriteXmlTopics;
			break;
		case SubscriptionOptions.ANPR:
			topics = anprTopics;
			break;
		case SubscriptionOptions.STELLAR_CELLEBRITE_XML:
			topics = stellarCellebriteXMLTopics;
			break;
		case SubscriptionOptions.AXIOM:
			topics = axiomTopics;
			break;
	}

	return fetchUtility(`${import.meta.env.VITE_API_URL}/kafka/subscribeToTopics`, {
		headers: createAPIHeaders(apiKey, 'POST'),
		method: 'POST',
		body: JSON.stringify({
			topics,
		}),
	});
};
